import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Group, List } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form/lib/types'
import { ExclamationTriangleIcon, InfoCircledIcon } from '@radix-ui/react-icons'
import { v4 as uuid } from 'uuid'

import { DrAttendeeInput } from './attendee/DrAttendeeInput'
import { CrAttendeeInput } from './CrForm/components/CrAttendeeInput'

import { getAttendeesWarnings } from '@/components/Event/attendeesWarnings'
import { CrFormStep } from '@/components/Event/CrForm/CrFormStep'
import { getSelectableWitnessSectionRoles } from '@/components/Event/CrForm/witnesses'
import { AttendeeRole } from '@/constants'
import { useAuth } from '@/hooks/useAuth'
import { AttendeeFormValues, ReporterFeedback, ZoomParticipant } from '@/types'

export const AttendeesInput: React.FC<{
  form: UseFormReturnType<ReporterFeedback>
  roles: AttendeeRole[]
  witnessIndex: number
  crLayout?: boolean
  witnessLocationEnabled?: boolean
  zoomParticipants?: ZoomParticipant[]
  timezone?: string
  onAddWitness?: () => void
  addWitnessDisabled?: boolean
}> = ({
  form,
  roles,
  witnessIndex,
  crLayout = false,
  witnessLocationEnabled = false,
  zoomParticipants = [],
  timezone,
  onAddWitness,
  addWitnessDisabled = false,
}) => {
  const [warnings, setWarnings] = useState<string[]>([])
  const [affectedFieldsWarnings, setAffectedFieldsWarnings] = useState<
    Set<keyof AttendeeFormValues>[]
  >([])
  const { user } = useAuth()
  const values = useMemo(
    () => form.values?.events_feedbacks[witnessIndex]?.attendees ?? [],
    [form.values, witnessIndex],
  )

  useEffect(() => {
    const { affectedFields, warnings } = getAttendeesWarnings(
      values,
      user?.email,
    )
    setWarnings(warnings)
    setAffectedFieldsWarnings(affectedFields)
  }, [values, user?.email])

  const addAttendee = () => {
    const feedbacks = form.values?.events_feedbacks || []
    const newAttendee = {
      id: uuid(),
      name: '',
      email: null,
      // use a default role as the field is not nullable & we use autosave
      role: roles[0],
      present: null,
    }

    for (let idx = witnessIndex; idx < feedbacks.length; idx++) {
      form.insertListItem(`events_feedbacks.${idx}.attendees`, newAttendee)
    }

    // The new row starts empty, so the step is no longer complete. Staged layout only —
    // the plain feedback view has no steps.
    if (crLayout) {
      form.setFieldValue(`steps.${CrFormStep.ATTENDANCE}.done`, false)
    }
  }

  return (
    <div className="flex">
      <div className={`flex-1 ${crLayout ? 'space-y-8' : 'space-y-4'}`}>
        {values.map((_, index) =>
          crLayout ? (
            <CrAttendeeInput
              key={index}
              roles={getSelectableWitnessSectionRoles({
                roles,
                attendees: values,
                attendeeIndex: index,
              })}
              form={form}
              index={index}
              attendeeWarnings={affectedFieldsWarnings}
              witnessIndex={witnessIndex}
              witnessLocationEnabled={witnessLocationEnabled}
              zoomParticipants={zoomParticipants}
              timezone={timezone}
            />
          ) : (
            <DrAttendeeInput
              key={index}
              roles={roles}
              form={form}
              index={index}
              attendeeWarnings={affectedFieldsWarnings}
              witnessIndex={witnessIndex}
            />
          ),
        )}
        {warnings.length === 0 && (
          <Alert
            icon={<InfoCircledIcon />}
            title="The pre-filled values may contain mistakes"
          >
            <p>Only submit valid person names and add all missing attendees.</p>
            <p>
              Remove words that aren’t actual names (esq, office etc.) from the
              above fields.
            </p>
          </Alert>
        )}
        {warnings.length > 0 && (
          <Alert icon={<ExclamationTriangleIcon />} color="orange">
            Please, double check the following attendee names and roles. They
            look a bit suspicious to us but we may be wrong:
            <List
              size="sm"
              mt="md"
              type="unordered"
              withPadding
              style={{ listStyle: 'disc' }}
            >
              {warnings.map((warning, idx) => (
                <List.Item key={idx}>{warning}</List.Item>
              ))}
            </List>
          </Alert>
        )}
        <Group spacing="sm">
          <Button
            type="button"
            color={crLayout ? undefined : 'gray'}
            variant={crLayout ? 'outline' : 'light'}
            onClick={addAttendee}
          >
            Add attendee
          </Button>
          {onAddWitness && (
            <Button
              type="button"
              variant="subtle"
              disabled={addWitnessDisabled}
              onClick={onAddWitness}
            >
              Add new witness
            </Button>
          )}
        </Group>
      </div>
    </div>
  )
}
